import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from "react";
import { Snackbar, Box, CircularProgress } from "@mui/material";
import { SnackbarCloseReason } from "@mui/material/Snackbar/Snackbar";
import { IconButton, Typography } from "@mui/joy";
import {
    CheckRounded,
    CloseRounded,
    PriorityHighRounded,
} from "@mui-symbols-material/w300";
import { useApolloClient, useQuery } from "@apollo/client";
import { BackgroundTask } from "../graphql/queries/backgroundTask";
import { BackgroundTaskStatus } from "../codegen/graphql";

type SnackbarStatus = "idle" | "progress" | "success" | "error";

interface SnackbarState {
    message: string;
    status: SnackbarStatus;
    progress: number;
    detail?: string;
    taskId?: string;
}

interface SnackbarContextProps {
    startProgress: (message: string, taskId: string) => void;
    startLocalProgress: (message: string) => void;
    updateLocalProgress: (current: number, total: number, detail?: string) => void;
    finishLocalProgress: (message: string) => void;
    errorLocalProgress: (message: string) => void;
    hideSnackbar: (
        event: React.SyntheticEvent<any> | Event,
        reason: SnackbarCloseReason
    ) => void;
}

const SnackbarContext = createContext<SnackbarContextProps | undefined>(
    undefined
);

export const useSnackbar = () => {
    const ctx = useContext(SnackbarContext);
    if (!ctx) throw new Error("useSnackbar must be used within SnackbarProvider");
    return ctx;
};

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState<SnackbarState>({
        message: "",
        status: "idle",
        progress: 0,
    });

    const client = useApolloClient();

    const { data, stopPolling, startPolling } = useQuery(BackgroundTask, {
        variables: { id: state.taskId ?? "" },
        skip: !state.taskId,
        pollInterval: 1000,
        fetchPolicy: "network-only",
    });

    // Realtime Status-Update
    useEffect(() => {
        if (!data?.backgroundTask) return;

        const { status, progressPercentage } = data.backgroundTask;

        // Update message based on status
        if (status === BackgroundTaskStatus.WAITING) {
            setState((prev) => ({
                ...prev,
                progress: progressPercentage ?? prev.progress,
                status: "progress",
                message: "Waiting...",
            }));
        } else if (status === BackgroundTaskStatus.ACTIVE) {
            setState((prev) => ({
                ...prev,
                progress: progressPercentage ?? prev.progress,
                status: "progress",
                message: "Uploading...",
            }));
        }

        if (status === BackgroundTaskStatus.FINISHED) {
            stopPolling();
            setState((prev) => ({
                ...prev,
                progress: 100,
                status: "success",
                message: "Import finished successfully!",
            }));
            client.refetchQueries({
                include: ["InstalledBaseNodeList"],
            });
        }

        if (
            status === BackgroundTaskStatus.FAILED ||
            status === BackgroundTaskStatus.CANCELLED
        ) {
            stopPolling();
            setState((prev) => ({
                ...prev,
                progress: 100,
                status: "error",
                message: "Import failed!",
            }));
        }
    }, [data, stopPolling]);

    const startProgress = (message: string, taskId: string) => {
        setState({ message, status: "progress", progress: 0, taskId });
        setOpen(true);
        startPolling(1000);
    };

    const startLocalProgress = (message: string) => {
        setState({ message, status: "progress", progress: 0 });
        setOpen(true);
    };

    const updateLocalProgress = (current: number, total: number, detail?: string) => {
        const progress = total > 0 ? Math.round((current / total) * 100) : 0;
        setState((prev) => ({ ...prev, progress, detail }));
    };

    const finishLocalProgress = (message: string) => {
        setState((prev) => ({ ...prev, progress: 100, status: "success", message, detail: undefined }));
    };

    const errorLocalProgress = (message: string) => {
        setState((prev) => ({ ...prev, progress: 100, status: "error", message, detail: undefined }));
    };

    const hideSnackbar = (
        event: React.SyntheticEvent<any> | Event,
        reason: SnackbarCloseReason
    ) => {
        if (reason !== "clickaway") {
            stopPolling();
            setOpen(false);
            setState({ message: "", status: "idle", progress: 0 });
        }
    };

    const getColor = () => {
        switch (state.status) {
            case "success":
                return "success";
            case "error":
                return "error";
            default:
                return "primary";
        }
    };

    return (
        <SnackbarContext.Provider
            value={{
                startProgress,
                startLocalProgress,
                updateLocalProgress,
                finishLocalProgress,
                errorLocalProgress,
                hideSnackbar,
            }}
        >
            {children}

            {open && (
                <Snackbar
                    open={open}
                    onClose={hideSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Box
                        sx={{
                            bgcolor: state.status === "success" ? "#d5f7e1" : "background.paper",
                            p: "12px 12px 24px 12px",
                            borderRadius: 1,
                            boxShadow: 3,
                            minHeight: 100,
                            minWidth: 390,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box display="flex" alignItems="center">
                            <Box position="relative" display="inline-flex" mt={2}>
                                <CircularProgress
                                    variant="determinate"
                                    color={getColor()}
                                    value={state.progress}
                                    size={70}
                                    thickness={4}
                                />
                                <Box
                                    top={0}
                                    left={0}
                                    bottom={0}
                                    right={0}
                                    position="absolute"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    {state.status === "progress" ? (
                                        <Typography level={"titleMedium"}>
                                            {`${Math.round(state.progress)}%`}
                                        </Typography>
                                    ) : state.status === "success" ? (
                                        <CheckRounded sx={{ fontSize: 36 }} color="success" />
                                    ) : state.status === "error" ? (
                                        <PriorityHighRounded sx={{ fontSize: 36 }} color="error" />
                                    ) : null}
                                </Box>
                            </Box>

                            <Box sx={{ pl: "16px", pt: "13px" }}>
                                <Typography level={"titleSmall"}>{state.message}</Typography>
                                {state.detail && (
                                    <Typography level={"bodySmall"} sx={{ color: "#666666" }}>
                                        {state.detail}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {state.status !== "progress" && (
                            <IconButton
                                sx={{ alignSelf: "flex-start" }}
                                onClick={(event) => hideSnackbar(event, "escapeKeyDown")}
                            >
                                <CloseRounded fontSize={"medium"} />
                            </IconButton>
                        )}
                    </Box>
                </Snackbar>
            )}
        </SnackbarContext.Provider>
    );
};
